import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useBrand } from "@/context/BrandContext";
import { KeyRound, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const { brand, logoSrc } = useBrand();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reset link");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0f17] grid-bg p-6">
      <form onSubmit={submit} className="w-full max-w-md sc-card p-8" data-testid="forgot-password-form">
        <div className="flex items-center gap-3 mb-6">
          <img src={logoSrc} alt={brand.product_name} className="w-14 h-14 object-contain mix-blend-screen" />
          <div>
            <div className="font-heading text-lg font-extrabold"><span className="text-slate-100">STEEL</span><span className="text-amber-400">FORGE AI</span></div>
            <div className="label-cap">Reset your password</div>
          </div>
        </div>
        {sent ? (
          <div className="text-sm text-slate-300 space-y-3" data-testid="forgot-password-sent">
            <MailCheck className="w-8 h-8 text-emerald-400" />
            <p>If an account exists for <span className="text-slate-100 font-mono">{email}</span>, a reset link is on its way. It expires in 30 minutes.</p>
            <p className="text-xs text-slate-500">Didn't get it? Check spam, or ask your workspace admin to issue a reset link from Administration.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-400 mb-4">Enter your account email and we'll send a one-time reset link.</p>
            <label className="block mb-5">
              <span className="label-cap">Email</span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2.5 text-slate-100 focus:outline-none focus:border-amber-500"
                data-testid="forgot-email-input" />
            </label>
            <button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold py-2.5 rounded-md transition disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="forgot-submit-button">
              <KeyRound className="w-4 h-4" /> {loading ? "Sending…" : "Send reset link"}
            </button>
          </>
        )}
        <div className="mt-5 text-sm text-slate-400 text-center">
          <Link to="/login" className="text-amber-400 hover:text-amber-300" data-testid="forgot-back-to-login">Back to sign in</Link>
        </div>
      </form>
    </div>
  );
}
